import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Store, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "owner" ? "owner" : "diner");
  const [form, setForm] = useState({ name: "", email: "", password: "", sales_code: (params.get("code") || "").toUpperCase() });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await register({ ...form, role });
      navigate(user.role === "owner" ? "/dashboard" : "/restaurants");
    } catch (err) {
      setError(fmtErr(err));
    }
    setLoading(false);
  };

  return (
    <div data-testid="register-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8">
        <h1 className="font-display text-3xl tracking-tight">Join TableDiary</h1>
        <div className="grid grid-cols-2 gap-3 mt-6">
          {[
            { key: "diner", icon: User, label: "I'm a diner" },
            { key: "owner", icon: Store, label: "I own a restaurant" },
          ].map((r) => (
            <button key={r.key} type="button" data-testid={`role-${r.key}-btn`} onClick={() => setRole(r.key)}
              className={`rounded-xl border p-4 text-left transition-colors duration-200 ${
                role === r.key ? "border-primary bg-primary/10" : "border-border/60 hover:border-border"
              }`}>
              <r.icon className={`w-5 h-5 ${role === r.key ? "text-primary" : "text-muted-foreground"}`} />
              <p className="text-sm mt-2">{r.label}</p>
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Full name</Label>
            <Input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 bg-background border-border/60" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Email</Label>
            <Input data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 bg-background border-border/60" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Password</Label>
            <Input data-testid="register-password-input" type="password" required minLength={8} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters.</p>
          </div>
          {role === "owner" && (
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Sales code (optional)</Label>
              <Input data-testid="register-sales-code-input" placeholder="Given by your TableDiary rep" value={form.sales_code}
                onChange={(e) => setForm({ ...form, sales_code: e.target.value })} className="mt-1.5 bg-background border-border/60" />
              <p className="text-xs text-muted-foreground mt-1">Working with our sales team? Add their code — we'll preload your venue with demo bookings to explore.</p>
              {params.get("code") && (
                <p data-testid="sales-code-prefilled" className="text-xs text-primary mt-1.5">Code applied from your rep's link — you're all set.</p>
              )}
            </div>
          )}
          {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
          <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full rounded-full">
            {loading ? "Creating account..." : role === "owner" ? "Create vendor account" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          Already have an account? <Link to="/login" data-testid="goto-login-link" className="text-primary hover:underline">Sign in</Link>
        </p>
      </motion.div>
    </div>
  );
}
